import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RECAPTCHA_ACTION_SAVE_CLIENT_DETAILS } from '../constants/checkout-security.constants';
import { WidgetRecaptchaSubmitPayload } from './recaptcha/widget-recaptcha.types';
import { WidgetRecaptchaService } from './recaptcha/widget-recaptcha.service';
import { WidgetCheckoutSecurityService } from './widget-checkout-security.service';
import { extractWidgetApiErrorCode } from './widget-api-error.util';

/** Conservative reuse window for pre-generated v3 tokens (Google allows ~2 minutes). */
const V3_TOKEN_MAX_AGE_MS = 90_000;

@Injectable({ providedIn: 'root' })
export class WidgetRecaptchaFlowService {
  private readonly recaptchaService = inject(WidgetRecaptchaService);
  private readonly checkoutSecurityService = inject(WidgetCheckoutSecurityService);

  readonly showV2Challenge = signal(false);

  private pendingV2Submit: ((captcha: WidgetRecaptchaSubmitPayload) => void) | null =
    null;

  private preparedContextKey: string | null = null;
  private securityEnabledForTenant = false;
  private cachedV3Token: string | null = null;
  private cachedV3PreparedAt: number | null = null;
  private prepareGeneration = 0;

  /**
   * Resolves tenant checkout-security flag and pre-generates a v3 token when enabled.
   * Call on tenant selection (or once when only one tenant is available).
   */
  async prepareForTenant(companyUuid: string, tenantUuid: string): Promise<void> {
    const company = companyUuid?.trim();
    const tenant = tenantUuid?.trim();
    if (!company || !tenant) {
      this.clearPreparedState();
      return;
    }

    const contextKey = `${company}:${tenant}`;
    const generation = ++this.prepareGeneration;
    this.preparedContextKey = contextKey;
    this.cachedV3Token = null;
    this.cachedV3PreparedAt = null;

    const securityEnabled = await this.isSecurityActive(company, tenant);
    if (generation !== this.prepareGeneration) {
      return;
    }

    this.securityEnabledForTenant = securityEnabled;
  }

  async beginSubmit(
    companyUuid: string,
    tenantUuid: string,
    submitFn: (captcha?: WidgetRecaptchaSubmitPayload) => void,
  ): Promise<void> {
    const company = companyUuid?.trim();
    const tenant = tenantUuid?.trim();
    const contextKey = company && tenant ? `${company}:${tenant}` : null;

    if (contextKey && contextKey !== this.preparedContextKey) {
      await this.prepareForTenant(company, tenant);
    }

    const securityEnabled =
      contextKey === this.preparedContextKey
        ? this.securityEnabledForTenant
        : await this.isSecurityActive(company ?? '', tenant ?? '');

    if (!securityEnabled) {
      submitFn(undefined);
      return;
    }

    if (this.isCachedV3TokenFresh()) {
      const token = this.cachedV3Token!;
      this.consumeCachedV3Token();
      submitFn({
        token,
        version: 'v3',
        action: RECAPTCHA_ACTION_SAVE_CLIENT_DETAILS,
      });
      return;
    }

    try {
      const token = await this.recaptchaService.executeV3(
        RECAPTCHA_ACTION_SAVE_CLIENT_DETAILS,
      );
      submitFn({
        token,
        version: 'v3',
        action: RECAPTCHA_ACTION_SAVE_CLIENT_DETAILS,
      });
    } catch {
      this.pendingV2Submit = submitFn;
      this.showV2Challenge.set(true);
    }
  }

  onV2Resolved(token: string): void {
    const submit = this.pendingV2Submit;
    this.pendingV2Submit = null;
    if (!submit) {
      this.showV2Challenge.set(false);
      return;
    }

    submit({
      token,
      version: 'v2',
    });
    this.showV2Challenge.set(false);
  }

  onV2Failed(): void {
    this.showV2Challenge.set(false);
    this.pendingV2Submit = null;
  }

  handleRecaptchaError(
    error: unknown,
    retrySubmit: (captcha: WidgetRecaptchaSubmitPayload) => void,
  ): boolean {
    if (extractWidgetApiErrorCode(error) !== 'recaptcha_challenge_required' && extractWidgetApiErrorCode(error) !== 'recaptcha_verification_failed') {
      return false;
    }

    this.consumeCachedV3Token();
    this.pendingV2Submit = retrySubmit;
    this.showV2Challenge.set(true);
    return true;
  }

  resetChallenge(): void {
    this.showV2Challenge.set(false);
    this.pendingV2Submit = null;
    this.consumeCachedV3Token();
  }

  private isCachedV3TokenFresh(): boolean {
    if (!this.cachedV3Token || this.cachedV3PreparedAt === null) {
      return false;
    }
    return Date.now() - this.cachedV3PreparedAt < V3_TOKEN_MAX_AGE_MS;
  }

  private consumeCachedV3Token(): void {
    this.cachedV3Token = null;
    this.cachedV3PreparedAt = null;
  }

  private clearPreparedState(): void {
    this.prepareGeneration++;
    this.preparedContextKey = null;
    this.securityEnabledForTenant = false;
    this.consumeCachedV3Token();
  }

  private async isSecurityActive(
    companyUuid: string,
    tenantUuid: string,
  ): Promise<boolean> {
    if (!this.recaptchaService.isConfigured()) {
      return false;
    }

    if (!companyUuid?.trim() || !tenantUuid?.trim()) {
      return false;
    }

    return firstValueFrom(
      this.checkoutSecurityService.isCheckoutSecurityEnabled(
        companyUuid,
        tenantUuid,
      ),
    );
  }
}

